import React, { useState, useEffect, useCallback } from 'react';
import './Clientes.css';
import { apiFetch, ApiError } from './api';
import { mensagemParaCodigo } from './mensagensErro';
import EstadoCarregando from './EstadoCarregando';
import EstadoErro from './EstadoErro';
import EstadoVazio from './EstadoVazio';

const ETAPAS_FUNIL = [
  { valor: 'PROSPECTO', label: 'Prospecto' },
  { valor: 'LEAD', label: 'Lead' },
  { valor: 'QUALIFICADO', label: 'Qualificado' },
  { valor: 'PROPOSTA', label: 'Proposta' },
  { valor: 'NEGOCIACAO', label: 'Negociação' },
  { valor: 'CONTRATO', label: 'Contrato' },
  { valor: 'CLIENTE', label: 'Cliente' },
  { valor: 'PERDIDO', label: 'Perdido' },
];

const LABEL_ETAPA = Object.fromEntries(ETAPAS_FUNIL.map(e => [e.valor, e.label]));

function etapaLabel(etapa) {
  return LABEL_ETAPA[etapa] || etapa || '—';
}

function tipoLabel(tipo) {
  if (tipo === 'PF') return 'Pessoa Física';
  if (tipo === 'PJ') return 'Pessoa Jurídica';
  return tipo || '—';
}

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

const FORM_VAZIO = {
  nome: '',
  tipo: 'PF',
  documento: '',
  email: '',
  telefone: '',
  etapaFunil: 'LEAD',
  observacoes: '',
  endereco: { logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', cep: '' },
};

function Clientes({ onNavegar }) {
  const [clientes, setClientes] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [filtroQ, setFiltroQ] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [incluirArquivados, setIncluirArquivados] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [clienteIdEdicao, setClienteIdEdicao] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [enviando, setEnviando] = useState(false);
  const [erroModal, setErroModal] = useState(null);

  const [confirmandoExclusao, setConfirmandoExclusao] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  const carregarClientes = useCallback(async (pg = 1, q = filtroQ, etapa = filtroEtapa, arquivados = incluirArquivados) => {
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams({ page: pg, perPage: 20, orderBy: 'nome', order: 'asc' });
      if (q.trim()) params.set('q', q.trim());
      if (etapa) params.set('etapaFunil', etapa);
      if (arquivados) params.set('incluirArquivados', 'true');
      const res = await apiFetch(`/api/clientes?${params}`);
      setClientes(res.data ?? []);
      setMetadata(res.metadata ?? null);
      setPagina(pg);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErro(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregando(false);
    }
  }, [filtroQ, filtroEtapa, incluirArquivados]);

  useEffect(() => {
    carregarClientes(1, filtroQ, filtroEtapa, incluirArquivados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEtapa, incluirArquivados]);

  const handleBuscar = (e) => {
    e.preventDefault();
    carregarClientes(1, filtroQ, filtroEtapa, incluirArquivados);
  };

  const limparFiltros = () => {
    setFiltroQ('');
    setFiltroEtapa('');
    setIncluirArquivados(false);
    carregarClientes(1, '', '', false);
  };

  const abrirModalNovo = () => {
    setForm(FORM_VAZIO);
    setModoEdicao(false);
    setClienteIdEdicao(null);
    setErroModal(null);
    setModalAberto(true);
  };

  const abrirModalEditar = async (cliente) => {
    setModoEdicao(true);
    setClienteIdEdicao(cliente.id);
    setErroModal(null);
    try {
      const det = await apiFetch(`/api/clientes/${cliente.id}`);
      const end = det.endereco || {};
      setForm({
        nome: det.nome || '',
        tipo: det.tipo || 'PF',
        documento: det.documento || '',
        email: det.email || '',
        telefone: det.telefone || '',
        etapaFunil: det.etapaFunil || 'LEAD',
        observacoes: det.observacoes || '',
        endereco: {
          logradouro: end.logradouro || '',
          numero: end.numero || '',
          complemento: end.complemento || '',
          bairro: end.bairro || '',
          cidade: end.cidade || '',
          uf: end.uf || '',
          cep: end.cep || '',
        },
      });
    } catch {
      setForm({
        ...FORM_VAZIO,
        nome: cliente.nome || '',
        tipo: cliente.tipo || 'PF',
        documento: cliente.documento || '',
        email: cliente.email || '',
        telefone: cliente.telefone || '',
        etapaFunil: cliente.etapaFunil || 'LEAD',
      });
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setErroModal(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEnderecoChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, endereco: { ...prev.endereco, [name]: value } }));
  };

  const salvarCliente = async () => {
    if (!form.nome.trim() || !form.documento.trim()) {
      setErroModal('Nome e documento são obrigatórios.');
      return;
    }
    setEnviando(true);
    setErroModal(null);
    const temEndereco = Object.values(form.endereco).some(v => v.trim());
    const corpo = {
      nome: form.nome.trim(),
      tipo: form.tipo,
      documento: form.documento.trim(),
      email: form.email.trim() || null,
      telefone: form.telefone.trim() || null,
      etapaFunil: form.etapaFunil || null,
      observacoes: form.observacoes.trim() || null,
      endereco: temEndereco ? form.endereco : null,
    };
    try {
      if (modoEdicao) {
        await apiFetch(`/api/clientes/${clienteIdEdicao}`, { method: 'PUT', body: corpo });
      } else {
        await apiFetch('/api/clientes', { method: 'POST', body: corpo });
      }
      fecharModal();
      carregarClientes(pagina, filtroQ, filtroEtapa, incluirArquivados);
    } catch (e) {
      setErroModal(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setEnviando(false);
    }
  };

  const excluirCliente = async () => {
    if (!confirmandoExclusao) return;
    setExcluindo(true);
    try {
      await apiFetch(`/api/clientes/${confirmandoExclusao.id}`, { method: 'DELETE' });
      setConfirmandoExclusao(null);
      carregarClientes(pagina, filtroQ, filtroEtapa, incluirArquivados);
    } catch (e) {
      alert(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setExcluindo(false);
    }
  };

  if (carregando) {
    return (
      <div className="clientes-container">
        <div className="clientes-header">
          <h1>Clientes</h1>
          <p className="descricao">Clientes e processos do seu escritório</p>
        </div>
        <EstadoCarregando mensagem="Carregando clientes..." />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="clientes-container">
        <div className="clientes-header">
          <h1>Clientes</h1>
          <p className="descricao">Clientes e processos do seu escritório</p>
        </div>
        <EstadoErro mensagem={erro} onRetry={() => carregarClientes(1, filtroQ, filtroEtapa, incluirArquivados)} />
      </div>
    );
  }

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Clientes</h1>
        <p className="descricao">Clientes e processos do seu escritório</p>
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <form className="filtros-principais" onSubmit={handleBuscar}>
          <div className="filtro-grupo">
            <label htmlFor="filtro-nome">Nome ou Documento</label>
            <input
              id="filtro-nome"
              type="text"
              placeholder="Buscar por nome ou documento"
              value={filtroQ}
              onChange={e => setFiltroQ(e.target.value)}
            />
          </div>

          <div className="filtro-grupo">
            <label htmlFor="filtro-etapa">Etapa do Funil</label>
            <select id="filtro-etapa" value={filtroEtapa} onChange={e => setFiltroEtapa(e.target.value)}>
              <option value="">Todas</option>
              {ETAPAS_FUNIL.map(e => (
                <option key={e.valor} value={e.valor}>{e.label}</option>
              ))}
            </select>
          </div>

          <div className="filtro-grupo filtro-checkbox">
            <label>
              <input
                type="checkbox"
                checked={incluirArquivados}
                onChange={e => setIncluirArquivados(e.target.checked)}
              />
              {' '}Incluir arquivados
            </label>
          </div>

          <div className="acoes-filtros">
            <button type="submit" className="btn-buscar">Buscar</button>
            <button type="button" className="limpar-filtros" onClick={limparFiltros}>
              Limpar
            </button>
            <button type="button" className="adicionar-cliente" onClick={abrirModalNovo}>
              + Adicionar Cliente
            </button>
          </div>
        </form>
      </div>

      {/* Lista */}
      {clientes.length > 0 ? (
        <>
          <div className="lista-clientes">
            <table className="tabela-clientes">
              <thead>
                <tr>
                  <th>Nome / Razão Social</th>
                  <th>Tipo</th>
                  <th>Documento</th>
                  <th>E-mail</th>
                  <th>Etapa</th>
                  <th>Marcas</th>
                  <th>Cadastro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map(cliente => (
                  <tr key={cliente.id}>
                    <td>{cliente.nome}</td>
                    <td>
                      <span className={`tipo-cliente ${cliente.tipo?.toLowerCase()}`}>
                        {tipoLabel(cliente.tipo)}
                      </span>
                    </td>
                    <td>{cliente.documento || '—'}</td>
                    <td>{cliente.email || '—'}</td>
                    <td>
                      <span className={`status-cliente ${cliente.etapaFunil?.toLowerCase()}`}>
                        {etapaLabel(cliente.etapaFunil)}
                      </span>
                    </td>
                    <td>
                      <span className="contador-marcas">{cliente.totalMarcas ?? 0}</span>
                    </td>
                    <td>{formatarData(cliente.criadoEm)}</td>
                    <td>
                      <div className="acoes-cliente">
                        <button
                          className="botao-acao visualizar"
                          onClick={() => onNavegar('detalhes-cliente', { clienteId: cliente.id })}
                        >
                          Visualizar
                        </button>
                        <button
                          className="botao-acao editar"
                          onClick={() => abrirModalEditar(cliente)}
                        >
                          Editar
                        </button>
                        <button
                          className="botao-acao excluir"
                          onClick={() => setConfirmandoExclusao(cliente)}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {metadata && metadata.totalPages > 1 && (
            <div className="paginacao">
              <button
                disabled={pagina <= 1}
                onClick={() => carregarClientes(pagina - 1, filtroQ, filtroEtapa, incluirArquivados)}
              >
                ‹ Anterior
              </button>
              <span>{pagina} / {metadata.totalPages} — {metadata.total} clientes</span>
              <button
                disabled={pagina >= metadata.totalPages}
                onClick={() => carregarClientes(pagina + 1, filtroQ, filtroEtapa, incluirArquivados)}
              >
                Próxima ›
              </button>
            </div>
          )}
        </>
      ) : (
        <EstadoVazio
          icone="fa-solid fa-users"
          titulo={
            filtroQ || filtroEtapa
              ? 'Nenhum cliente encontrado'
              : 'Comece pelo seu primeiro cliente'
          }
          descricao={
            filtroQ || filtroEtapa
              ? 'Nenhum cliente corresponde aos filtros aplicados.'
              : 'Cadastre um cliente para acompanhar marcas, processos e prazos do INPI num só lugar.'
          }
          acao={{ rotulo: '+ Adicionar Cliente', onClick: abrirModalNovo }}
        />
      )}

      {/* Modal Adicionar / Editar */}
      {modalAberto && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>{modoEdicao ? 'Editar Cliente' : 'Adicionar Cliente'}</h2>
              <button className="modal-fechar" onClick={fecharModal}>×</button>
            </div>

            <div className="modal-corpo">
              {erroModal && <div className="erro-modal">{erroModal}</div>}

              <div className="tipo-pessoa">
                {['PF', 'PJ'].map(t => (
                  <div className="tipo-radio" key={t}>
                    <input
                      type="radio"
                      id={`tipo-${t}`}
                      name="tipo"
                      value={t}
                      checked={form.tipo === t}
                      onChange={handleFormChange}
                    />
                    <label htmlFor={`tipo-${t}`}>
                      {t === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                    </label>
                  </div>
                ))}
              </div>

              <div className="form-cliente">
                <div className="form-grupo">
                  <label htmlFor="nome">
                    {form.tipo === 'PF' ? 'Nome Completo' : 'Razão Social'} *
                  </label>
                  <input id="nome" name="nome" type="text" value={form.nome} onChange={handleFormChange} />
                </div>

                <div className="form-grupo">
                  <label htmlFor="documento">{form.tipo === 'PF' ? 'CPF' : 'CNPJ'} *</label>
                  <input id="documento" name="documento" type="text" value={form.documento} onChange={handleFormChange} />
                </div>

                <div className="form-grupo">
                  <label htmlFor="etapaFunil">Etapa do Funil</label>
                  <select id="etapaFunil" name="etapaFunil" value={form.etapaFunil} onChange={handleFormChange}>
                    {ETAPAS_FUNIL.map(e => (
                      <option key={e.valor} value={e.valor}>{e.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-grupo">
                  <label htmlFor="email">E-mail</label>
                  <input id="email" name="email" type="email" value={form.email} onChange={handleFormChange} />
                </div>

                <div className="form-grupo">
                  <label htmlFor="telefone">Telefone</label>
                  <input id="telefone" name="telefone" type="text" value={form.telefone} onChange={handleFormChange} />
                </div>

                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="observacoes">Observações</label>
                  <textarea
                    id="observacoes"
                    name="observacoes"
                    rows="2"
                    value={form.observacoes}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-grupo form-grupo--endereco" style={{ gridColumn: '1 / -1' }}>
                  <label>Endereço</label>
                  <div className="form-endereco">
                    <input placeholder="Logradouro" name="logradouro" value={form.endereco.logradouro} onChange={handleEnderecoChange} />
                    <input placeholder="Número" name="numero" value={form.endereco.numero} onChange={handleEnderecoChange} style={{ maxWidth: '80px' }} />
                    <input placeholder="Complemento" name="complemento" value={form.endereco.complemento} onChange={handleEnderecoChange} />
                    <input placeholder="Bairro" name="bairro" value={form.endereco.bairro} onChange={handleEnderecoChange} />
                    <input placeholder="Cidade" name="cidade" value={form.endereco.cidade} onChange={handleEnderecoChange} />
                    <input placeholder="UF" name="uf" value={form.endereco.uf} onChange={handleEnderecoChange} style={{ maxWidth: '60px' }} />
                    <input placeholder="CEP" name="cep" value={form.endereco.cep} onChange={handleEnderecoChange} style={{ maxWidth: '100px' }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModal} disabled={enviando}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarCliente} disabled={enviando}>
                {enviando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmação de Exclusão */}
      {confirmandoExclusao && (
        <div className="modal-overlay" onClick={() => setConfirmandoExclusao(null)}>
          <div className="modal-conteudo modal-confirmacao" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Confirmar Exclusão</h2>
              <button className="modal-fechar" onClick={() => setConfirmandoExclusao(null)}>×</button>
            </div>
            <div className="modal-corpo">
              <p>
                Tem certeza que deseja excluir o cliente{' '}
                <strong>{confirmandoExclusao.nome}</strong>? Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="modal-rodape">
              <button
                className="botao-cancelar"
                onClick={() => setConfirmandoExclusao(null)}
                disabled={excluindo}
              >
                Cancelar
              </button>
              <button className="botao-excluir" onClick={excluirCliente} disabled={excluindo}>
                {excluindo ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clientes;
